import { useEffect, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { i18nService } from '../../services/i18n';
import type { MemoryFileScope } from '../../../shared/memoryFile';
import { useMemoryFileEditor } from '../../hooks/useMemoryFileEditor';

/** 文件编辑器只接收业务 ID；关闭或切换账号后忽略迟到结果，失败时保留草稿。 */
export default function MemoryFileEditor({ id, scope = 'secretary', onClose, onSaved }: {
  id: string; scope?: MemoryFileScope; onClose: () => void; onSaved: (id: string) => void;
}) {
  const { snapshot, draft, setDraft, revision, preview, busy, error, changed, load, save, rename, clearPreview } = useMemoryFileEditor(id, scope, onSaved);
  const [newId, setNewId] = useState(id);
  const t = (key: string) => i18nService.t(`memoryFile_${key}`);
  useEffect(() => { setNewId(snapshot?.id ?? id); }, [snapshot?.id, id]);
  /** 离开和重新加载会丢弃未保存草稿，必须由用户明确选择。 */
  const discard = (action: () => void) => {
    if (!busy && (!changed || window.confirm(t('discardConfirm')))) action();
  };
  const button = 'rounded-lg border px-3 py-2 text-sm disabled:opacity-40';
  return <Dialog open onClose={() => discard(onClose)} className="relative z-[150]">
    <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
    <div className="fixed inset-0 flex items-center justify-center p-3">
      <Dialog.Panel className="flex max-h-[90dvh] w-full max-w-3xl flex-col gap-3 overflow-y-auto rounded-xl bg-claude-bg dark:bg-claude-darkBg p-4 text-claude-text dark:text-claude-darkText">
        <Dialog.Title className="font-semibold">{t('title')} · {snapshot?.id ?? id}</Dialog.Title>
        <Dialog.Description className="text-xs opacity-70">{t(scope === 'soul' ? 'soulHint' : 'hint')}</Dialog.Description>
        {error && <p role="alert" className="text-sm text-red-500">{error}</p>}
        {scope !== 'soul' && <div className="flex flex-wrap items-end gap-2">
          <label className="min-w-0 flex-1 text-sm">{t('fileName')}
            <input aria-label={t('fileName')} value={newId} maxLength={120} disabled={busy || !snapshot || Boolean(revision)}
              onChange={event => setNewId(event.target.value)} className="mt-1 block w-full rounded border bg-transparent px-2 py-1.5" />
          </label>
          <button className={button} disabled={busy || !snapshot || changed || Boolean(revision) || !newId || newId === snapshot.id}
            onClick={() => { if (window.confirm(t('renameConfirm'))) void rename(newId); }}>{t('rename')}</button>
          <p className="w-full text-xs opacity-70">{changed ? t('saveBeforeRename') : t('renameHint')}</p>
        </div>}
        <label className="text-sm">{t('history')}
          <select className="ml-2 max-w-full rounded border bg-transparent p-1" disabled={busy || !snapshot}
            value={revision} onChange={event => {
              if (event.target.value) void load(event.target.value);
              else clearPreview();
            }}>
            <option value="">{t('current')}</option>
            {snapshot?.revisions.map(item => <option key={item.id} value={item.id}>
              {new Date(item.createdAt).toLocaleString()} · {t(item.actor === 'restore' ? 'restoreActor' : item.actor === 'rename' ? 'renameActor' : item.actor)} · {item.id.slice(-4)}
            </option>)}
          </select>
        </label>
        <label className="flex min-h-0 flex-col gap-1 text-sm">{revision ? t('preview') : t('content')}
          <textarea aria-label={revision ? t('preview') : t('content')} value={revision ? preview ?? '' : draft}
            readOnly={Boolean(revision)} disabled={busy || !snapshot} maxLength={65536}
            onChange={event => setDraft(event.target.value)} spellCheck={false}
            className="h-[45dvh] min-h-32 w-full resize-y rounded-lg border bg-transparent p-3 font-mono text-sm" />
        </label>
        <div className="flex flex-wrap justify-end gap-2">
          {snapshot?.defaultContent !== undefined && <button className={button} disabled={busy}
            onClick={() => discard(() => { clearPreview(); setDraft(snapshot.defaultContent!); })}>{i18nService.t('secretarySoulReset')}</button>}
          <button className={button} disabled={busy} onClick={() => discard(() => void load())}>{t('reload')}</button>
          <button className={button} disabled={busy} onClick={() => discard(onClose)}>{i18nService.t('close')}</button>
          <button className={`${button} bg-claude-accent text-white`} disabled={busy || !snapshot || (revision ? preview === null : !changed || !draft.trim())}
            onClick={() => { if (!revision || window.confirm(t('restoreConfirm'))) void save(Boolean(revision)); }}>{busy ? i18nService.t('loading') : revision ? t('restore') : i18nService.t('save')}</button>
        </div>
      </Dialog.Panel>
    </div>
  </Dialog>;
}
